<template>
  <Modal
    ref="modal"
    @close="$emit('close')"
  >
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('HeadContract.ProgressClaimsShowLite.ClaimAttachmentsModal.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="max-h-[40rem] overflow-y-auto w-full max-w-3xl min-w-lg relative">
        <FileUpload
          v-model="fileUploadForm"
          @change="fileUploaded"
        />

        <div
          class="flex justify-between items-center mt-4 w-full"
        >
          <div class="flex items-center space-x-1 font-semibold mb-8">
            <span class="text-lg">{{ $t('HeadContract.ProgressClaimsShowLite.ClaimAttachmentsModal.attachments') }}</span>

            <AppInfoHover tooltip="Images will be attached to the monthly financial report and PDFs will be appended to the certificate." />
          </div>
        </div>

        <AppSpinner
          v-if="claimMediaCollection.mediaRequestAction.is(States.LOADING)"
          class="mt-4 w-full flex justify-center"
        />

        <div
          v-else-if="claimMediaCollection.mediaCollection.value.length === 0"
          class="font-semibold text-center"
        >
          {{ $t('HeadContract.ProgressClaimsShowLite.ClaimAttachmentsModal.noAttachmentsFound') }}
        </div>


        <div class="relative pb-5">
          <button
            v-if="claimMediaCollection.mediaCollection.value.length > 1"
            class="absolute left-0 bottom-1/2 border border-black rounded-full w-8 h-8 flex justify-center items-center hover:bg-gray-100 active:bg-gray-200 z-10 bg-white"
            type="button"
            @click="mediaSwiper.slidePrev()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
  <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
</svg>
          </button>

          <Swiper
            class="max-w-md"
            :space-between="20"
            :loop="claimMediaCollection.mediaCollection.value.length > 1"
            centered-slides
            :modules="[Pagination]"
            :pagination="{
              el: '.swiper-pagination',
              type: 'bullets'
            }"
            @swiper="setSwiper"
          >
            <SwiperSlide
              v-for="image in claimMediaCollection.mediaCollection.value"
              :key="image.uuid"
              class="!h-auto"
            >
              <div class="flex items-center h-full w-full justify-center">
                <MediaDisplay
                  :media="image"
                  class="w-60"
                  :delete-action="destroyMedia"
                  @delete="claimMediaCollection.removeMedia(image.uuid)"
                  @refresh="claimMediaCollection.refreshMedia(image.uuid)"
                />
              </div>
            </SwiperSlide>
          </Swiper>

          <div class="swiper-pagination" />

          <button
            v-if="claimMediaCollection.mediaCollection.value.length > 1"
            class="absolute right-0 bottom-1/2 border border-black rounded-full w-8 h-8 flex justify-center items-center hover:bg-gray-100 active:bg-gray-200 z-10 bg-white"
            type="button"
            @click="mediaSwiper.slideNext()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
          </button>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-start p-2 space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="modal.close"
        >
          {{ $t('HeadContract.ProgressClaimsShowLite.ClaimAttachmentsModal.button.close') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import MediaDisplay from '@/components/MediaDisplay.vue';
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { useMediaCollection } from '@/composables/useMediaCollection';
import { useSignedData } from '@/composables/useSignedData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { parseUrlParams } from '@/helpers';
import { useClaimsStore } from '@/store/claims';
import Swal from 'sweetalert2';
import { Pagination } from 'swiper/modules';
import { Swiper as SwiperInterface } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { computed, ref, useTemplateRef, watch } from 'vue';

const props = defineProps<{
  claimId: Id;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const claimsStore = useClaimsStore();
const signedData = useSignedData();
const fileUploadForm = ref();
const storeMediaAction = useStoreApiAction(claimsStore.storeSubcontractorClaimMedia);
const claimMediaCollection = useMediaCollection(claimsStore.viewSubcontractorClaimMedia);
const destroyMediaAction = useStoreApiAction(claimsStore.destroySubcontractorClaimMedia);

const mediaSignedParams = computed(() => {
  return parseUrlParams(signedData.meta.attach_media_url as string);
});

const viewMediaSignedParams = computed(() => {
  return parseUrlParams(signedData.meta.view_media_url as string);
});

const destroyMediaSignedParams = computed(() => {
  return parseUrlParams(signedData.meta.destroy_media_url as string);
});

const destroyMedia = (uuid: string) => {
  return destroyMediaAction.request(props.claimId, destroyMediaSignedParams.value, { media: uuid });
};

watch(() => props.claimId, (newValue) => {
  if(newValue) {
    claimMediaCollection.fetchMedia(newValue, viewMediaSignedParams.value);
  }
}, { immediate: true });

const fileUploaded = (event: Event) => {
  const files = Array.from((event.target as HTMLInputElement).files);
  const selectedFile = files[0];

  if(selectedFile) {
    const fileFormData = new FormData();

    fileFormData.append('images[]', selectedFile);

    storeMediaAction.request(props.claimId, mediaSignedParams.value, fileFormData).then((medias) => {
      claimMediaCollection.addMedia(medias);
      fileUploadForm.value = [];
    }).catch((error) => {
      if(error.status === 422) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to upload file',
          text: error.data.message,
        });
      }
    });
  }
};

const mediaSwiper = ref<SwiperInterface>();

const setSwiper = (swiper: SwiperInterface) => {
  mediaSwiper.value = swiper;
};

const modal = useTemplateRef('modal');
</script>

<style scoped></style>
